import { authServiceApi } from "../../../app/authServiceApi";
import { API_URL } from "../../../constants/apiUrl";
import {
  CommonResponse,
  GetQueryArgsType,
  PatchQueryArgsType,
} from "../../../types";
import {
  GetSurveyResponse,
  GetSurveyResponses,
  TGetAllSurveyTemplate,
  TGetSurveyAnswerListParams,
  TGetSurveyData,
  TGetSurveyDataParams,
  TGetSurveyResponsesByMemberParams,
  TGetSurveyResponsesParams,
  TGetSurveyTemplateDetails,
  TGetSurveyTemplateParams,
} from "./survey.type";

export const surveyApi = authServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    createSurveyTemplate: builder.mutation({
      query: (data) => ({
        url: API_URL.createSurveyTemplate,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllSurveyTemplate"];
        return [];
      },
    }),
    surveyTemplateUpdateBulkStatus: builder.mutation({
      query: (data) => ({
        url: API_URL.surveyTemplateUpdateBulkStatus,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetAllSurveyTemplate"];
        return [];
      },
    }),
    updateSurveyTemplate: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ id, body }) => ({
        url: `${API_URL.updateSurveyTemplate}/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetAllSurveyTemplate",
            { type: "GetSurveyTemplateDetails", id },
          ];
        return [];
      },
    }),
    getAllSurveyTemplate: builder.query<
      CommonResponse<TGetAllSurveyTemplate>,
      GetQueryArgsType<TGetSurveyTemplateParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllSurveyTemplate,
        ...queryArgs,
      }),
      providesTags: ["GetAllSurveyTemplate"],
    }),
    getSurveyTemplateDetails: builder.query<
      CommonResponse<TGetSurveyTemplateDetails>,
      number
    >({
      query: (id) => ({
        url: `${API_URL.getSurveyTemplateDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetSurveyTemplateDetails", id }],
    }),
    getSurveyAnswerList: builder.query<
      CommonResponse<GetSurveyResponses>,
      GetQueryArgsType<TGetSurveyAnswerListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.getSurveyAnswerList}`,
        ...queryArgs,
      }),
      providesTags: ["GetAllSurveyAnswerList"],
    }),
    getSurveyData: builder.query<
      CommonResponse<TGetSurveyData>,
      GetQueryArgsType<TGetSurveyDataParams>
    >({
      query: (queryArgs) => {
        return {
          url: `${API_URL.getSurveyData}`,
          ...queryArgs,
        };
      },
      providesTags: () => ["GetSurveyData"],
    }),
    getSurveyResponses: builder.query<
      CommonResponse<GetSurveyResponse>,
      GetQueryArgsType<TGetSurveyResponsesParams>
    >({
      query: (queryArgs) => {
        return {
          url: `${API_URL.getSurveyResponses}`,
          ...queryArgs,
        };
      },
      providesTags: () => ["GetSurveyResponses"],
    }),
    getSurveyResponsesByMember: builder.query<
      CommonResponse<GetSurveyResponses>,
      GetQueryArgsType<TGetSurveyResponsesByMemberParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.getSurveyResponseByMember}`,
        ...queryArgs,
      }),
      providesTags: ["GetAllSurveyAnswerList"],
    }),
    updateSurveyAnswersStatus: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ body }) => ({
        url: `${API_URL.updateSurveyAnswerStatus}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["GetAllSurveyAnswerList"],
    }),
  }),
});

export const {
  useCreateSurveyTemplateMutation,
  useSurveyTemplateUpdateBulkStatusMutation,
  useUpdateSurveyTemplateMutation,
  useGetAllSurveyTemplateQuery,
  useGetSurveyTemplateDetailsQuery,
  useGetSurveyAnswerListQuery,
  useGetSurveyDataQuery,
  useGetSurveyResponsesQuery,
  useGetSurveyResponsesByMemberQuery,
  useUpdateSurveyAnswersStatusMutation
} = surveyApi;
